import React from 'react';
import styled from 'styled-components';
import SvgFragment from '../../../fragments/SvgFragment';
import {
    IInviteLandingResponse,
    getWorkspaceInitials,
    isInviteSsoRegistration,
} from '../../../interfaces/models/invite';
import { InviteWorkspaceName } from './InviteJoinTitle';
import { styles } from '../../../styles/themes/style';
import {
    AuthMethodButton,
    AuthOnboardingActions,
    AuthOnboardingCard,
    AuthOnboardingCardBody,
    AuthOnboardingIntro,
    DividerLine,
    OrDivider,
    OrText,
} from '../authFormCommon';
import AuthSignUpAgreementText from './AuthSignUpAgreementText';
import AuthSignUpLoginPrompt from './AuthSignUpLoginPrompt';

interface IInviteSignupMethodStepProps {
    landing: IInviteLandingResponse;
    inviteCode?: string;
    loading: boolean;
    onGoogleSignUp: () => void;
    onEmailSignUp: () => void;
    onSsoSignUp: () => void;
}

const InviteSignupMethodStep: React.FC<IInviteSignupMethodStepProps> = ({
    landing,
    inviteCode,
    loading,
    onGoogleSignUp,
    onEmailSignUp,
    onSsoSignUp,
}) => {
    const workspaceName = landing.invite?.workspace?.name || 'Workspace';
    const invitedEmail = landing.invite?.email?.trim();
    const workspaceInitials = getWorkspaceInitials(workspaceName);
    const showSso = isInviteSsoRegistration(landing);

    return (
        <AuthOnboardingCard>
            <WorkspaceHero>
                <WorkspaceAvatar aria-hidden>{workspaceInitials}</WorkspaceAvatar>
                <WorkspaceDetails>
                    <InviteBadge>Workspace invite</InviteBadge>
                    <InviteWorkspaceName>{workspaceName}</InviteWorkspaceName>
                </WorkspaceDetails>
            </WorkspaceHero>

            {/* {invitedEmail && (
                <InvitedEmailRow>
                    <EmailIcon aria-hidden>
                        <SvgFragment type="email" />
                    </EmailIcon>
                    <InvitedEmailText>
                        Invited as <strong>{invitedEmail}</strong>
                    </InvitedEmailText>
                </InvitedEmailRow>
            )} */}

            <AuthOnboardingCardBody>
                <AuthOnboardingIntro>
                    Register to collaborate with your team, discover shared connections, and manage
                    relationships together in LeadDelta.
                </AuthOnboardingIntro>

                <AuthOnboardingActions>
                    <AuthMethodButton
                        onClickHandler={onGoogleSignUp}
                        disabled={loading}
                        $type="blue"
                        tooltipMessage=""
                    >
                        <SvgFragment type="google" />
                        Register with Google
                    </AuthMethodButton>
                    <OrDivider>
                        <DividerLine />
                        <OrText>Or</OrText>
                        <DividerLine />
                    </OrDivider>
                    <AuthMethodButton
                        $type="light"
                        tooltipMessage=""
                        onClickHandler={onEmailSignUp}
                    >
                        Register with Email
                    </AuthMethodButton>
                    {showSso && (
                        <AuthMethodButton
                            $type="light"
                            tooltipMessage=""
                            onClickHandler={onSsoSignUp}
                        >
                            Register with SSO
                        </AuthMethodButton>
                    )}
                    <AuthSignUpLoginPrompt inviteCode={inviteCode} />
                    <AuthSignUpAgreementText />
                </AuthOnboardingActions>
            </AuthOnboardingCardBody>
        </AuthOnboardingCard>
    );
};

const WorkspaceHero = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 14px;
    width: 100%;
    padding: 16px;
    box-sizing: border-box;
    background: ${styles.colors.primary100};
    border: 1px solid ${styles.colors.primary200};
    border-radius: 10px;
`;

const WorkspaceAvatar = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: ${styles.colors.primary600};
    font-family: NunitoSans;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    color: ${styles.colors.white};
`;

const WorkspaceDetails = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    min-width: 0;
`;

const InviteBadge = styled.span`
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 10px;
    border-radius: 12px;
    background: ${styles.colors.white};
    border: 1px solid ${styles.colors.primary200};
    font-family: NunitoSans;
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
    color: ${styles.colors.primary600};
`;

const InvitedEmailRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 12px 14px;
    box-sizing: border-box;
    background: ${styles.colors.black100};
    border-radius: 8px;
`;

const EmailIcon = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${styles.colors.black400};

    & .svg {
        width: 16px;
        height: 16px;
    }
`;

const InvitedEmailText = styled.p`
    margin: 0;
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: ${styles.colors.black400};
    word-break: break-word;

    strong {
        font-weight: 700;
        color: ${styles.colors.black600};
    }
`;

export default InviteSignupMethodStep;
